import { Shield, Check, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { hasNonMonthlyPayCycle, calculatePerPaycheckAmount, getPayCycleLabel } from "@/lib/payrollUtils";
import { PermissionButton } from "@/components/ui/permission-button";
import { amountOrPlaceholder } from "@/lib/loadingPlaceholder";

export interface CoverageSegmentCounts {
  enrolled: number;
  pending: number;
  waived: number;
  notYet: number;
}

interface CoverageHeaderBannerProps {
  /** Total number of plan-type slots the client offers for this employee. */
  totalTypes: number;
  /** How many of those have any election on file (active/pending/waived). */
  configuredTypes: number;
  segments: CoverageSegmentCounts;
  /** Monthly employee premium total for currently-effective elections. */
  totalMonthly: number;
  payCycle?: string | null;
  /** Enrollments still loading: show a dash, never "$0.00" (BUG-008). */
  isLoading?: boolean;
  /** DEV-382: plans that can't be priced. Non-empty → the total is withheld, not summed as $0. */
  unpricedPlans?: string[];
  /**
   * Configure Benefits CTA. Rendered inside the banner's right block when
   * `remainingCount > 0`. Pass `undefined` to suppress the CTA entirely
   * (e.g., when the global wizard is intentionally hidden for this view).
   */
  configureCta?: {
    remainingCount: number;
    onClick: () => void;
  };
}

const SEGMENT_STYLE: Record<keyof CoverageSegmentCounts, { fill: string; dot: string; label: string }> = {
  enrolled: { fill: "bg-violet-600", dot: "bg-violet-600", label: "Enrolled" },
  pending:  { fill: "bg-violet-300", dot: "bg-violet-300", label: "Pending" },
  waived:   { fill: "bg-stone-400", dot: "bg-stone-400", label: "Waived" },
  notYet:   { fill: "bg-amber-500", dot: "bg-amber-500", label: "Not yet" },
};

function formatUSD(n: number) {
  if (!Number.isFinite(n)) return "$0.00";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export function CoverageHeaderBanner({
  totalTypes,
  configuredTypes,
  segments,
  totalMonthly,
  payCycle,
  isLoading = false,
  unpricedPlans = [],
  configureCta,
}: CoverageHeaderBannerProps) {
  const incomplete = !isLoading && unpricedPlans.length > 0;
  const pct = totalTypes > 0 ? Math.round((configuredTypes / totalTypes) * 100) : 0;
  const allDone = totalTypes > 0 && configuredTypes >= totalTypes;
  const isNonMonthly = hasNonMonthlyPayCycle(payCycle);
  const perPaycheck = isNonMonthly ? calculatePerPaycheckAmount(totalMonthly, payCycle) : totalMonthly;

  const total = Math.max(1, segments.enrolled + segments.pending + segments.waived + segments.notYet);

  return (
    <div
      className={cn(
        "rounded-xl border bg-gradient-to-r from-violet-100 via-violet-50 to-white",
        "border-violet-200 dark:border-violet-800/40",
        "dark:from-violet-950/40 dark:via-violet-950/20 dark:to-stone-900/40",
        "border-l-4 border-l-violet-500",
        "p-4 md:p-5 flex flex-col md:flex-row gap-4 md:gap-6",
      )}
    >
      {/* Left: progress + segments */}
      <div className="flex-1 min-w-0 space-y-3">
        <div className="flex items-center gap-3">
          <div className="h-7 w-7 rounded-md bg-white border border-violet-200 dark:bg-violet-950/60 dark:border-violet-800/60 flex items-center justify-center">
            <Shield className="h-4 w-4 text-violet-600 dark:text-violet-400" />
          </div>
          <div className="text-[15px] font-bold text-violet-900 dark:text-violet-100">
            Current Benefit Elections
          </div>
          {totalTypes > 0 && (
            <div className="text-xs font-mono text-violet-700/80 dark:text-violet-300/80">
              {configuredTypes} of {totalTypes} configured ({pct}%)
            </div>
          )}
        </div>

        {/* Stacked progress bar */}
        <div className="h-3 w-full rounded-full border border-violet-200 dark:border-violet-800/60 bg-white/60 dark:bg-violet-950/40 overflow-hidden flex">
          {(["enrolled", "pending", "waived", "notYet"] as const).map((key) => {
            const count = segments[key];
            if (count === 0) return null;
            const widthPct = (count / total) * 100;
            return (
              <div
                key={key}
                className={cn(SEGMENT_STYLE[key].fill, "h-full")}
                style={{ width: `${widthPct}%`, minWidth: count > 0 ? 8 : 0 }}
                aria-label={`${SEGMENT_STYLE[key].label}: ${count}`}
              />
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 text-xs">
          {(["enrolled", "pending", "waived", "notYet"] as const).map((key) => {
            const count = segments[key];
            return (
              <div
                key={key}
                className={cn("flex items-center gap-1.5", count === 0 && "opacity-40")}
              >
                <span className={cn("h-2 w-2 rounded-sm inline-block", SEGMENT_STYLE[key].dot)} />
                <span className="text-violet-900/80 dark:text-violet-200/80">{SEGMENT_STYLE[key].label}</span>
                <span className="font-mono font-semibold text-violet-900 dark:text-violet-100">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="hidden md:block w-px bg-violet-200 dark:bg-violet-800/60 shrink-0" />

      {/* Right: paycheck headline + done chip */}
      <div className="md:min-w-[200px] flex flex-col justify-between gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-wide font-semibold text-violet-700/80 dark:text-violet-300/80">
            {isNonMonthly ? `Per ${getPayCycleLabel(payCycle)}` : "Per month"}
          </div>
          <div
            className="text-2xl md:text-[26px] font-bold font-mono tracking-tight text-violet-950 dark:text-violet-50"
            data-testid="coverage-banner-per-period"
            data-loading={isLoading ? "true" : undefined}
          >
            {incomplete ? "Unavailable" : amountOrPlaceholder(isLoading, () => formatUSD(perPaycheck))}
          </div>
          {incomplete ? (
            <div className="text-[11px] text-amber-700 dark:text-amber-400 max-w-[240px]" data-testid="coverage-banner-unpriced">
              Can't total: {unpricedPlans.join(", ")} couldn't be priced — see the benefit card for the reason.
            </div>
          ) : isNonMonthly && (
            <div className="text-[11px] font-mono text-violet-700/80 dark:text-violet-300/80">
              {amountOrPlaceholder(isLoading, () => formatUSD(totalMonthly))}/mo
            </div>
          )}
        </div>
        {configureCta && configureCta.remainingCount > 0 ? (
          <PermissionButton
            permission="clients:edit"
            whenDenied="hide"
            size="sm"
            className="gap-1.5 bg-violet-600 hover:bg-violet-700 text-white shadow-sm self-start"
            onClick={configureCta.onClick}
          >
            <Plus className="h-3.5 w-3.5" />
            Configure remaining ({configureCta.remainingCount})
          </PermissionButton>
        ) : allDone ? (
          <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-violet-800 dark:text-violet-200 bg-white/70 dark:bg-violet-950/40 border border-violet-200 dark:border-violet-800/60 rounded-full px-2.5 py-1 self-start">
            <Check className="h-3 w-3" />
            Every type has an election on file.
          </div>
        ) : null}
      </div>
    </div>
  );
}
